<template>
  <div>
    <v-container v-if="isLoading">
      <v-row>
        <v-col>
          <v-skeleton-loader type="button"></v-skeleton-loader>
        </v-col>
      </v-row>
      <v-row>
        <v-col class="shrink" v-for="i in 15" :key="i">
          <v-skeleton-loader type="chip"></v-skeleton-loader>
        </v-col>
      </v-row>
      <v-row>
        <v-col>
          <v-skeleton-loader transition="fab-transition" type="image"></v-skeleton-loader>
        </v-col>
      </v-row>
    </v-container>
    <v-container v-else>
      <v-row class="d-flex justify-center align-center">
        <v-col>
          <router-link :to="{ name: 'Home' }">
            <v-btn rounded text>
              <v-icon left>mdi-chevron-left</v-icon>
              Back
            </v-btn>
          </router-link>
        </v-col>
        <v-col class="d-flex justify-end">
          <v-btn @click="toggleShowAddToGoogleInstructions" rounded text>
            <v-icon left>mdi-information</v-icon> Add to Your Google Calendar
          </v-btn>
        </v-col>
      </v-row>
      <v-row v-if="showAddToGoogleInstructions">
        <v-col>
          <v-alert v-model="showAddToGoogleInstructions" type="info" dismissible>
            <v-row>
              <v-col>
                To view LMS sessions on your Google Calendar, copy the link below and follow the instructions
              </v-col>
            </v-row>
            <v-row>
              <v-col>
                <small>
                  <pre>https://airtable.com/shrbUVxVCCtFY3RVd/iCal?timeZone=America%2FNew_York&userLocale=en</pre>
                </small>
              </v-col>
            </v-row>
            <v-row>
              <v-col class="d-flex justify-end">
                <a target="_blank" href="https://support.airtable.com/hc/en-us/articles/225050348-Integrating-Airtable-with-external-calendar-applications-e-g-Google-Calendar-Microsoft-Outlook-Apple-Calendar-">
                  <v-btn rounded>
                     <v-icon left>mdi-open-in-new</v-icon> Instructions
                  </v-btn>
                </a>
              </v-col>
            </v-row>
          </v-alert>
        </v-col>
      </v-row>
      <v-row>
        <v-col>
          <v-chip :color="curriculum.fields.Color" dark small class="ma-1" v-for="curriculum in curriculums" :key="curriculum.id">
            {{ curriculum.fields.Name }}
          </v-chip>
        </v-col>
      </v-row>
      <v-row>
        <v-col>
          <v-card shaped>
            <v-card-text>
              <v-row>
                <v-col>
                  <v-row class="d-flex justy-space-between align-center">
                    <v-col>
                      <v-btn
                        icon
                        class="ma-2"
                        rounded
                        @click="$refs.calendar.prev()"
                      >
                        <v-icon>mdi-chevron-left</v-icon>
                      </v-btn>
                    </v-col>
                    <v-col>
                      <v-select
                        v-model="type"
                        :items="types"
                        dense
                        outlined
                        hide-details
                        class="ma-2"
                        label="view"
                      ></v-select>
                    </v-col>
                    <v-col class="d-flex justify-end">
                      <v-btn
                        icon
                        rounded
                        class="ma-2"
                        @click="$refs.calendar.next()"
                      >
                        <v-icon>mdi-chevron-right</v-icon>
                      </v-btn>
                    </v-col>
                  </v-row>
                  <v-spacer></v-spacer>
                  <v-sheet height="600">
                    <v-calendar
                      ref="calendar"
                      v-model="value"
                      :weekdays="weekday"
                      :type="type"
                      :events="events"
                      :event-overlap-mode="mode"
                      :event-overlap-threshold="30"
                      :event-color="getEventColor"
                      @change="getSessions"
                      @click:event="goToSession"
                      @click:more="viewDay"
                      @click:date="viewDay"
                    ></v-calendar>
                  </v-sheet>
                </v-col>
              </v-row>
            </v-card-text>
          </v-card>
        </v-col>
      </v-row>
    </v-container>
  </div>
</template>

<script>
export default {
  name: 'Calendar',

  data: () => ({
    loadingCurriculums: true,
    loadingSessions: true,
    type: 'month',
    types: ['month', 'week', 'day', '4day'],
    mode: 'stack',
    weekday: [0, 1, 2, 3, 4, 5, 6],
    value: '',
    events: [],
    colors: ['blue', 'indigo', 'deep-purple', 'cyan', 'green', 'orange', 'grey darken-1'],
    showAddToGoogleInstructions: false,
  }),

  computed: {
    curriculums () {
      const c = this.$store.getters.getCurriculums
      return c.sort((a, b) => {
        return a.fields.Name > b.fields.Name ? 1 : -1
      })
      .map(c => {
        c.color = this.colors[this.rnd(0, this.colors.length - 1)]
        return c
      })
    },
    isLoading () {
      return this.curriculums.length === 0 || this.sessions.length === 0
    },
    sessions () {
      return this.$store.getters.getSessions
    }
  },

  async created () {
    await this.$store.dispatch('getRecords', {
      table: 'Curriculums',
      view: 'All Curriculums',
      dispatch: 'setCurriculums'
    })
    await this.$store.dispatch('getRecords', {
      table: 'Sessions',
      view: 'All Sessions',
      dispatch: 'setSessions'
    })
    // a hack to get events to show up after events are loaded
    await this.$refs.calendar.next()
    this.$refs.calendar.prev()
    // end of hack
  },

  methods: {
    getSessions (event) {
      if (!event) return []
      const { start, end } = event
      const min = new Date(`${start.date}T00:00:00`)
      const max = new Date(`${end.date}T23:59:59`)
      const seshs = this.sessions.filter(s => {
        const hasStartDate = s.fields.Start
        const hasEndDate = s.fields.End
        const startDateInFilter = new Date(s.fields.Start) >= min
        const endDateInFilter = new Date(s.fields.End) <= max
        return hasStartDate && hasEndDate && startDateInFilter && endDateInFilter
      })
      .map(s => {
        return {
          id: s.id,
          name: s.fields.Name,
          start: new Date(s.fields.Start),
          end: new Date(s.fields.End),
          color: this.getSessionColor(s),
          timed: true
        }
      })
      return this.events = seshs
    },
    getSessionColor (session) {
      const curriculum = this.curriculums.find(c => {
        return session.fields.Curriculum.includes(c.id)
      })
      return curriculum.fields.Color
    },
    getEventColor (event) {
      return event.color
    },
    goToSession ({ event}) {
      this.$router.push({ name: 'Session', params: { id: event.id } })
    },
    rnd (a, b) {
      return Math.floor((b - a + 1) * Math.random()) + a
    },
    toggleShowAddToGoogleInstructions () {
      return this.showAddToGoogleInstructions = !this.showAddToGoogleInstructions
    },
    viewDay ({ date }) {
      this.type = 'day'
      this.value = date
      this.getSessions()
    }
  },
}
</script>
